/**
 * DocOutline - Right sidebar with TOC, Comments, and AI assistant
 *
 * Features:
 * - Tabbed interface: Table of Contents / Comments / Related
 * - Collapsible hierarchical Table of Contents
 * - Comments panel (Notion/Google Docs style)
 * - Active heading tracking with auto-expand
 * - Sidebar collapse/expand toggle with localStorage persistence
 * - "Talk with Martha" AI assistant (collapsed by default, bottom ~1/3)
 * - Related documents section
 * - Document metadata (author, updated, word count)
 *
 * Uses ReaderDetailPanelShell for visual consistency with ContentMetadataSidebar.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Hash, Clock, User, FileText, ChevronRight, MessageCircle, List, Link2 } from 'lucide-react';
import { resolveInitialContent, type EditorDocument, type DocumentLinkWithTarget } from '../../api/documents';
import type { InlineComment, CommentReply } from './InlineComment';
import { CommentsSidebar } from './CommentsSidebar';
import { RelatedSidebar } from './RelatedSidebar';
import { ReaderDetailPanelShell, type TabDefinition } from '../shared/ReaderDetailPanelShell';

export interface TOCItem {
  id: string;
  title: string;
  level: number;
}

// Tree node structure for hierarchical TOC
export interface TOCNode {
  item: TOCItem;
  children: TOCNode[];
}

interface DocOutlineProps {
  document?: EditorDocument;
  className?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  // Comments props
  comments?: InlineComment[];
  repliesMap?: Map<string, CommentReply[]>;
  activeCommentId?: string | null;
  onCommentClick?: (comment: InlineComment) => void;
  onCommentReply?: (commentId: string, content: string) => void;
  onCommentResolve?: (commentId: string) => void;
  isCommentSubmitting?: boolean;
  onAskMartha?: (context: { selectedText: string; commentContent: string }) => void;
  // Links props
  documentLinks?: DocumentLinkWithTarget[];
  linksCount?: number;
  onDeleteLink?: (relationId: string) => void;
  onAddLink?: () => void;
  onLinkDocClick?: (docId: string) => void;
  isLinkDeleting?: boolean;
  // Issues props
  onIssueClick?: (issueId: string) => void;
}

type TabType = 'toc' | 'comments' | 'related';

// Extract headings from markdown content
function extractHeadings(markdown: string): TOCItem[] {
  const headingRegex = /^(#{1,3})\s+(.+)$/gm;
  const headings: TOCItem[] = [];
  let match;

  while ((match = headingRegex.exec(markdown)) !== null) {
    const level = match[1].length;
    const title = match[2].trim();
    const id = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    headings.push({ id, title, level });
  }

  return headings;
}

// Build tree structure from flat headings list
function buildTOCTree(headings: TOCItem[]): TOCNode[] {
  const tree: TOCNode[] = [];
  const stack: { node: TOCNode; level: number }[] = [];

  for (const item of headings) {
    const newNode: TOCNode = { item, children: [] };

    // Pop nodes from stack that are at same or higher level
    while (stack.length > 0 && stack[stack.length - 1].level >= item.level) {
      stack.pop();
    }

    if (stack.length === 0) {
      // This is a root-level heading
      tree.push(newNode);
    } else {
      // Add as child of the current parent
      stack[stack.length - 1].node.children.push(newNode);
    }

    // Push this node onto the stack
    stack.push({ node: newNode, level: item.level });
  }

  return tree;
}

// Find all ancestor IDs for a given heading ID
function findAncestorIds(tree: TOCNode[], targetId: string, path: string[] = []): string[] | null {
  for (const node of tree) {
    if (node.item.id === targetId) {
      return path;
    }
    if (node.children.length > 0) {
      const result = findAncestorIds(node.children, targetId, [...path, node.item.id]);
      if (result) return result;
    }
  }
  return null;
}

// Collapsible TOC Tree Item component
interface TOCTreeItemProps {
  node: TOCNode;
  activeHeading: string | null;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  onScrollTo: (id: string) => void;
  depth?: number;
}

function TOCTreeItem({
  node,
  activeHeading,
  expandedIds,
  onToggleExpand,
  onScrollTo,
  depth = 0,
}: TOCTreeItemProps) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.item.id);
  const isActive = activeHeading === node.item.id;

  return (
    <li>
      <div className="flex items-center">
        {/* Expand/collapse button */}
        {hasChildren ? (
          <button
            onClick={() => onToggleExpand(node.item.id)}
            className="p-0.5 rounded hover:bg-neutral-100 transition-colors flex-shrink-0"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <ChevronRight
              size={14}
              className={`text-neutral-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
            />
          </button>
        ) : (
          <span className="w-[18px]" /> // Spacer for alignment
        )}

        {/* Heading link */}
        <button
          onClick={() => onScrollTo(node.item.id)}
          className={`
            flex-1 flex items-center gap-1.5 px-1.5 py-1 text-sm rounded transition-colors text-left truncate
            ${isActive
              ? 'text-coral-600 font-medium bg-coral-50'
              : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
            }
          `}
        >
          <Hash size={10} className="text-neutral-300 flex-shrink-0" />
          <span className="truncate">{node.item.title}</span>
        </button>
      </div>

      {/* Children (collapsed by default) */}
      {hasChildren && isExpanded && (
        <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-neutral-200 pl-1">
          {node.children.map((child) => (
            <TOCTreeItem
              key={child.item.id}
              node={child}
              activeHeading={activeHeading}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              onScrollTo={onScrollTo}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// Calculate word count
function countWords(text: string): number {
  return text
    .replace(/[#*`_\[\]()]/g, '')
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
}

// Format date
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Estimate read time (assuming 200 wpm)
function estimateReadTime(wordCount: number): string {
  const minutes = Math.ceil(wordCount / 200);
  return `${minutes} min read`;
}

/**
 * DocOutlineContent — Standalone TOC tab content for use in the global sidebar.
 * Extracts headings, builds tree, tracks active heading via IntersectionObserver.
 */
export function DocOutlineContent({ document: doc }: { document?: EditorDocument }) {
  const [activeHeading, setActiveHeading] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toc = useMemo(() => {
    if (!doc?.content) return [];
    return extractHeadings(resolveInitialContent(doc));
  }, [doc?.content]);

  const tocTree = useMemo(() => buildTOCTree(toc), [toc]);

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    if (activeHeading && tocTree.length > 0) {
      const ancestors = findAncestorIds(tocTree, activeHeading);
      if (ancestors && ancestors.length > 0) {
        setExpandedIds((prev) => {
          const next = new Set(prev);
          ancestors.forEach((id) => next.add(id));
          return next;
        });
      }
    }
  }, [activeHeading, tocTree]);

  const stats = useMemo(() => {
    if (!doc?.content) return { wordCount: 0, readTime: '0 min read' };
    const wordCount = countWords(resolveInitialContent(doc));
    return { wordCount, readTime: estimateReadTime(wordCount) };
  }, [doc?.content]);

  useEffect(() => {
    if (!toc.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveHeading(entry.target.id);
        });
      },
      { rootMargin: '-80px 0px -80% 0px' },
    );
    toc.forEach((item) => {
      const el = window.document.getElementById(item.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [toc]);

  const scrollToHeading = (id: string) => {
    const el = window.document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!doc) {
    return (
      <div className="p-3 space-y-3">
        <div className="animate-pulse space-y-2">
          <div className="h-3 bg-neutral-100 rounded w-3/4" />
          <div className="h-3 bg-neutral-100 rounded w-1/2" />
          <div className="h-3 bg-neutral-100 rounded w-2/3" />
          <div className="h-3 bg-neutral-50 rounded w-3/5" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-4">
      {tocTree.length > 0 && (
        <div>
          <ul className="space-y-0.5">
            {tocTree.map((node) => (
              <TOCTreeItem
                key={node.item.id}
                node={node}
                activeHeading={activeHeading}
                expandedIds={expandedIds}
                onToggleExpand={handleToggleExpand}
                onScrollTo={scrollToHeading}
              />
            ))}
          </ul>
        </div>
      )}
      <div className="pt-3 border-t border-neutral-100">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <Clock size={12} />
            <span>{stats.readTime}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <FileText size={12} />
            <span>{stats.wordCount.toLocaleString()} words</span>
          </div>
          {doc.created_by && (
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <User size={12} />
              <span>{doc.created_by}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-xs text-neutral-400">
            <span>Updated {formatDate(doc.updated_at)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DocOutline({
  document,
  className = '',
  isCollapsed = false,
  onToggleCollapse,
  // Comments props
  comments = [],
  repliesMap = new Map(),
  activeCommentId = null,
  onCommentClick,
  onCommentReply,
  onCommentResolve,
  isCommentSubmitting,
  onAskMartha,
  // Links props
  documentLinks = [],
  linksCount = 0,
  onDeleteLink,
  onAddLink,
  onLinkDocClick,
  isLinkDeleting,
  // Issues props
  onIssueClick,
}: DocOutlineProps) {
  const [activeHeading, setActiveHeading] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<TabType>('toc');

  // Extract TOC from document content
  const toc = useMemo(() => {
    if (!document?.content) return [];
    return extractHeadings(resolveInitialContent(document));
  }, [document?.content]);

  // Build hierarchical tree from flat headings
  const tocTree = useMemo(() => buildTOCTree(toc), [toc]);

  // Toggle expand/collapse for a TOC item
  const handleToggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Auto-expand ancestors when active heading changes
  useEffect(() => {
    if (activeHeading && tocTree.length > 0) {
      const ancestors = findAncestorIds(tocTree, activeHeading);
      if (ancestors && ancestors.length > 0) {
        setExpandedIds((prev) => {
          const next = new Set(prev);
          ancestors.forEach((id) => next.add(id));
          return next;
        });
      }
    }
  }, [activeHeading, tocTree]);

  // Calculate document stats
  const stats = useMemo(() => {
    if (!document?.content) return { wordCount: 0, readTime: '0 min read' };
    const wordCount = countWords(resolveInitialContent(document));
    return {
      wordCount,
      readTime: estimateReadTime(wordCount),
    };
  }, [document?.content]);

  // Intersection observer for active heading
  useEffect(() => {
    if (!toc.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeading(entry.target.id);
          }
        });
      },
      { rootMargin: '-80px 0px -80% 0px' }
    );

    // Observe all heading elements
    toc.forEach((item) => {
      const element = window.document.getElementById(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [toc]);

  // Smooth scroll to heading
  const scrollToHeading = (id: string) => {
    const element = window.document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Count open comments for tab badge (safe with empty defaults)
  const openCommentsCount = comments.filter((c) => c.status === 'open').length;
  // Check if document has blockers for attention dot
  const hasBlockers = documentLinks.some(l => l.relation_type === 'blocked_by');

  const tabs: TabDefinition[] = useMemo(() => [
    { id: 'toc', icon: <List size={16} />, title: 'Contents' },
    {
      id: 'comments',
      icon: <MessageCircle size={16} />,
      title: 'Comments',
      badge: openCommentsCount > 0,
      badgeColor: 'coral' as const,
    },
    {
      id: 'related',
      icon: <Link2 size={16} />,
      title: 'Related',
      badge: hasBlockers,
      badgeColor: 'amber' as const,
    },
  ], [openCommentsCount, hasBlockers]);

  const bottomSection = undefined;

  return (
    <ReaderDetailPanelShell
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={(id) => setActiveTab(id as TabType)}
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse || (() => {})}
      resizable
      minWidth={240}
      maxWidth={480}
      widthStorageKey="doc-outline-width"
      bottomSection={bottomSection}
      className={className}
    >
      {/* Tab Content */}
      {!document ? (
        /* Loading skeleton — keeps shell in layout while document loads */
        <div className="p-3 space-y-3">
          <div className="animate-pulse space-y-2">
            <div className="h-3 bg-neutral-100 rounded w-3/4" />
            <div className="h-3 bg-neutral-100 rounded w-1/2" />
            <div className="h-3 bg-neutral-100 rounded w-2/3" />
            <div className="h-3 bg-neutral-50 rounded w-3/5" />
          </div>
        </div>
      ) : activeTab === 'toc' ? (
        <div className="p-3 space-y-4">
          {/* Collapsible Table of Contents */}
          {tocTree.length > 0 && (
            <div>
              <ul className="space-y-0.5">
                {tocTree.map((node) => (
                  <TOCTreeItem
                    key={node.item.id}
                    node={node}
                    activeHeading={activeHeading}
                    expandedIds={expandedIds}
                    onToggleExpand={handleToggleExpand}
                    onScrollTo={scrollToHeading}
                  />
                ))}
              </ul>
            </div>
          )}

          {/* Document Stats */}
          <div className="pt-3 border-t border-neutral-100">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-neutral-500">
                <Clock size={12} />
                <span>{stats.readTime}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-neutral-500">
                <FileText size={12} />
                <span>{stats.wordCount.toLocaleString()} words</span>
              </div>
              {document.created_by && (
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <User size={12} />
                  <span>{document.created_by}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-neutral-400">
                <span>Updated {formatDate(document.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'comments' ? (
        /* Comments Tab Content */
        <CommentsSidebar
          comments={comments}
          repliesMap={repliesMap}
          activeCommentId={activeCommentId}
          onCommentClick={onCommentClick || (() => {})}
          onReply={onCommentReply || (() => {})}
          onResolve={onCommentResolve || (() => {})}
          onAskMartha={onAskMartha}
          isSubmitting={isCommentSubmitting}
        />
      ) : (
        /* Related Tab Content */
        <RelatedSidebar
          links={documentLinks ?? []}
          onDeleteLink={onDeleteLink || (() => {})}
          onAddLink={onAddLink || (() => {})}
          onLinkDocClick={onLinkDocClick || (() => {})}
          isDeleting={isLinkDeleting}
          onIssueClick={onIssueClick}
        />
      )}
    </ReaderDetailPanelShell>
  );
}

export default DocOutline;
